import { useCallback, useEffect, useRef } from "react";

/**
 * A floated image with text running around it looks broken when only a line or two spill out
 * below the image: every other line stops at the image, those few run the full column width
 * and read as an accident. Further down the same page, or one breakpoint up, the same block
 * drops five or six lines below the image and looks entirely deliberate — so this cannot be
 * fixed in the content, only at the width where it happens.
 *
 * CSS has nothing for it (`orphans`/`widows` apply to fragmentation, not to floats), so this
 * measures the tail the wrap leaves below the image. When it is only a line or two, the text
 * is given its own block formatting context, which stops it wrapping under the image at all
 * and leaves two clean columns. At every other width the wrap is left untouched.
 */

/** A tail this short reads as an accident rather than a deliberate wrap. */
const MAX_ORPHAN_LINES = 2;

/** The elements whose line boxes make up the running text of a block. */
const TEXT_SELECTOR = "p, h3, h4";

const lineHeightOf = (element: HTMLElement) => {
	const { lineHeight, fontSize } = window.getComputedStyle(element);
	const parsed = parseFloat(lineHeight);
	return Number.isNaN(parsed) ? parseFloat(fontSize) * 1.5 : parsed;
};

/**
 * The tops of one element's rendered lines that sit below `boundary`, in document order.
 *
 * Only text nodes are measured, never whole elements. Ranging over an element returns the
 * border box of every inline it contains as well, and a `<strong>` or link box starts at the
 * text ascent rather than at the top of its line — a few pixels lower, thanks to the
 * half-leading that `leading-relaxed` adds — which is enough to mistake markup on the last
 * line beside the image for a line that had dropped below it.
 */
const lineTopsBelow = (element: HTMLElement, boundary: number) => {
	const tops: number[] = [];
	const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
	const range = document.createRange();

	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		if (!node.nodeValue?.trim()) continue;

		range.selectNodeContents(node);
		Array.from(range.getClientRects()).forEach((rect) => {
			// Compare line centres: a line that merely straddles the bottom edge of the image
			// is still reading as beside it, not below it.
			if (rect.height > 0 && rect.top + rect.height / 2 >= boundary) tops.push(rect.top);
		});
	}

	return tops.sort((a, b) => a - b);
};

/**
 * Measures the orphan tail: how many lines the *first* paragraph to reach past the image
 * leaves below it.
 *
 * Counting every line below the image is the wrong measure. Where a block's second paragraph
 * begins below the image it scores high — six, in the executive summary block — while what
 * actually reads as broken there is the single line finishing the paragraph that straddles
 * the image bottom, stranded on its own before the paragraph break. A paragraph that starts
 * below the image is full width from its first line and looks deliberate however long it is,
 * so it must not mask the tail above it.
 */
const countOrphanTail = (container: HTMLElement, boundary: number) => {
	const elements = Array.from(container.querySelectorAll<HTMLElement>(TEXT_SELECTOR));

	for (const element of elements) {
		const tops = lineTopsBelow(element, boundary);
		if (tops.length === 0) continue;

		// One line splits into a rect per text node, so cluster the tops back into lines.
		const lineHeight = lineHeightOf(element);
		return tops.filter((top, index) => index === 0 || top - tops[index - 1] > lineHeight / 2).length;
	}

	return 0;
};

export function useAvoidFloatOrphan<F extends HTMLElement, T extends HTMLElement>() {
	const floatRef = useRef<F>(null);
	const textRef = useRef<T>(null);

	const measure = useCallback(() => {
		const floatElement = floatRef.current;
		const textElement = textRef.current;
		if (!floatElement || !textElement) return;

		// Always measure the wrapping layout. Once the fix is applied nothing spills below the
		// image any more, so measuring the fixed state would count zero orphans and the block
		// could never go back to wrapping when the viewport changes.
		textElement.style.display = "";
		// Exposed so the behaviour can be checked in devtools without instrumenting anything.
		textElement.dataset.floatWrap = "on";

		const { display, float } = window.getComputedStyle(floatElement);
		// Below `sm` the image is a full-width block (or hidden), so there is no float to wrap around.
		if (display === "none" || float === "none" || floatElement.offsetHeight === 0) return;

		const linesBelow = countOrphanTail(textElement, floatElement.getBoundingClientRect().bottom);
		if (linesBelow > 0 && linesBelow <= MAX_ORPHAN_LINES) {
			textElement.style.display = "flow-root";
			textElement.dataset.floatWrap = "off";
		}
	}, []);

	useEffect(() => {
		const floatElement = floatRef.current;
		if (!floatElement) return;

		let frame = 0;
		// Measuring mutates layout, so defer to the next frame to keep the observer quiet.
		const schedule = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(measure);
		};

		// The float's own size tracks everything that matters here: the viewport width (its
		// width is a fraction of the column) and the image finishing loading (its height).
		const observer = new ResizeObserver(schedule);
		observer.observe(floatElement);
		document.fonts?.ready.then(schedule).catch(() => undefined);

		return () => {
			cancelAnimationFrame(frame);
			observer.disconnect();
		};
	}, [measure]);

	return { floatRef, textRef };
}
